import { twMerge } from "tailwind-merge";
import { useState, useEffect } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useLocalStorage } from "react-use";
import TimezoneSelect, {
  ITimezone,
  ITimezoneOption,
} from "react-timezone-select";

import "react-calendar/dist/Calendar.css";
import { getTimezone } from "@/shared";
import { toast } from "react-hot-toast";
import { ImportJsonPresets } from "@/components/import-json-presets";
import { PlusIcon } from "@radix-ui/react-icons";
import {
  Popover,
  Select,
  Tooltip,
  Button as RButton,
  IconButton,
  Text,
  Separator,
} from "@radix-ui/themes";
import { useUrlQueries } from "@/shared/hooks/useUrlQueries";

interface DatePickerProps {
  className?: string;
  value?: [Dayjs | null, Dayjs | null];
  showPresetSelector?: boolean;
  showTimezoneSelector?: boolean;
  timezone?: ITimezone;
  minDate?: Date;
  maxDate?: Date;
  onChange?: (
    value: [dayjs.Dayjs | null, dayjs.Dayjs | null],
    timezone: string | ITimezone
  ) => void;
}

const presetMap: Record<
  string,
  {
    start: Date;
    end: Date;
  }
> = {
  "1": {
    start: new Date("2023-06-19T17:07:18+03:00"),
    end: new Date("2023-06-19T17:08:11+03:00"),
  },
  "2": {
    start: new Date("2023-06-19T17:09:31+03:00"),
    end: new Date("2023-06-19T17:10:32+03:00"),
  },
};

const defaultPresetsList = Object.entries(presetMap).map(([key, value]) => ({
  value: key,
  label: `${dayjs(value.start).format("MMM DD, HH:mm:ss")} - ${dayjs(
    value.end
  ).format("HH:mm:ss")}`,
}));

export function NativeDatePicker({
  className,
  onChange,
  value = [null, null],
  minDate,
  maxDate,
  showPresetSelector = true,
  showTimezoneSelector = true,
}: DatePickerProps) {
  const { getQuery, setQueries, removeQueries } = useUrlQueries();
  const urlStartDate = getQuery("start");
  const urlEndDate = getQuery("end");
  const utcTimezone = "Etc/GMT";

  const [open, setOpen] = useState(false);
  const [timezone, setTimezone] = useState<string | ITimezoneOption>(
    getTimezone()
  );
  const [timestampFrom, setTimestampFrom] = useState(urlStartDate || "");
  const [timestampTo, setTimestampTo] = useState(urlEndDate || "");

  const [innerValue, setInnerValue] =
    useState<[Dayjs | null, Dayjs | null]>(value);

  const [preset, setPreset] = useState<string>("");
  const [presets, setValue] = useLocalStorage(
    "date-presets",
    defaultPresetsList
  );

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === "ArrowLeft" ||
      e.key === "ArrowRight" ||
      e.key === "ArrowUp" ||
      e.key === "ArrowDown" ||
      e.key === "Home" ||
      e.key === "End"
    ) {
      e.stopPropagation();
    }
  };

  useEffect(() => {
    if (urlStartDate && urlEndDate) {
      setTimeout(() => {
        setTimezone(utcTimezone);
        setInnerValue([dayjs(urlStartDate), dayjs(urlEndDate)]);
        onApply([innerValue[0], innerValue[1]], utcTimezone);
      });
    }
  }, []);

  const rangeIsEmpty = !innerValue[0] && !innerValue[1];

  const onAddPreset = () => {
    if (innerValue[0]?.isValid() && innerValue[1]?.isValid()) {
      return savePreset(innerValue[0], innerValue[1], timezone as string);
    }

    const input = prompt("Enter ISO timestamps separated with comma");
    const [start, end] = input?.split(",") || [];
    const startDate = dayjs(start);
    const endDate = dayjs(end);

    if (!input || !(startDate.isValid() && endDate.isValid())) {
      alert("Invalid input");
      return;
    }

    savePreset(startDate, endDate);
    onApply([startDate, endDate], timezone);
  };

  const generatePresetId = (
    startDate: Dayjs,
    endDate: Dayjs,
    timezone = ""
  ) => {
    return (
      startDate?.toISOString() + "__" + endDate?.toISOString() + "__" + timezone
    );
  };

  const savePreset = (startDate: Dayjs, endDate: Dayjs, timezone?: string) => {
    setValue((currentValue = []) => [
      ...currentValue,
      {
        value: generatePresetId(startDate, endDate, timezone),
        label: `${startDate.format("MMM DD, HH:mm:ss")} - ${endDate.format(
          "HH:mm:ss"
        )}`,
      },
    ]);
    toast.success("Date ranges saved to presets");
  };

  const onApply = (
    [startDate, endDate]: [dayjs.Dayjs | null, dayjs.Dayjs | null],
    timezone: ITimezone
  ) => {
    if (timestampFrom || timestampTo) {
      if (!timestampFrom || !timestampTo) {
        return toast.error("Please, specify date range");
      }

      const startDate = dayjs(timestampFrom);
      const endDate = dayjs(timestampTo);

      if (!startDate.isValid() || !endDate.isValid()) {
        alert("Invalid input");
        return;
      }

      setQueries({
        start: startDate?.toISOString(),
        end: endDate?.toISOString(),
      });

      onChange?.([startDate, endDate], timezone);
      setOpen(false);
      return;
    }

    if (!startDate || !endDate) {
      return toast.error("Please, specify date range");
    }

    const tz = typeof timezone === "string" ? timezone : timezone?.value;
    const innerValueWithTimezone: typeof innerValue = [
      dayjs(startDate).tz(tz, true),
      dayjs(endDate).tz(tz, true),
    ];

    if (
      innerValueWithTimezone[0]?.toISOString() &&
      innerValueWithTimezone[1]?.toISOString()
    ) {
      setQueries({
        start: innerValueWithTimezone[0]?.toISOString(),
        end: innerValueWithTimezone[1]?.toISOString(),
      });
    } else {
      removeQueries(["start", "end"]);
    }

    onChange?.(innerValueWithTimezone, timezone);
    setOpen(false);
  };

  const onClear = () => {
    setInnerValue([null, null]);
    removeQueries(["start", "end"]);
    onChange?.([null, null], "");
    setTimestampFrom("");
    setTimestampTo("");
    setOpen(false);
    setPreset("");
  };

  const isError =
    !!innerValue[0] &&
    !!innerValue[1] &&
    innerValue[0].toDate() >= innerValue[1].toDate();

  const clampDate = (date: Dayjs) => {
    const bufferedMinDate = minDate ? dayjs(minDate).startOf("day") : null;
    const bufferedMaxDate = maxDate ? dayjs(maxDate).endOf("day") : null;

    if (bufferedMinDate && date.isBefore(bufferedMinDate))
      return bufferedMinDate;
    if (bufferedMaxDate && date.isAfter(bufferedMaxDate))
      return bufferedMaxDate;
    return date;
  };

  const getBufferedMinDate = () =>
    minDate ? dayjs(minDate).startOf("day") : null;
  const getBufferedMaxDate = () =>
    maxDate ? dayjs(maxDate).endOf("day") : null;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger>
        <RButton
          variant="soft"
          color="gray"
          size="3"
          radius="large"
          className={twMerge("select-none text-sm leading-6", className)}
        >
          {rangeIsEmpty ? (
            "Select range"
          ) : (
            <>
              {innerValue[0]?.format("DD.MM.YYYY HH:mm:ss")}
              {" - "}
              {innerValue[1]?.format("DD.MM.YYYY HH:mm:ss")}
            </>
          )}
        </RButton>
      </Popover.Trigger>
      <Popover.Content className="p-0 w-[350px]">
        <div className="p-4">
          <Text size="4" mb="4" className="font-semibold">
            Select date range
          </Text>
          <div className="mb-5">
            <label htmlFor="from" className="block text-sm mb-2">
              From
            </label>
            <div className="flex gap-2 mb-2">
              <input
                id="from"
                step="1"
                value={
                  innerValue[0]
                    ? innerValue[0].format("YYYY-MM-DDTHH:mm:ss")
                    : ""
                }
                onChange={(event) => {
                  setInnerValue((currentValue) => {
                    const newFrom = clampDate(dayjs(event.target.value));
                    return [
                      newFrom,
                      currentValue[1] && currentValue[1] > newFrom
                        ? currentValue[1]
                        : clampDate(dayjs(event.target.value).add(1, "second")),
                    ];
                  });
                }}
                className={twMerge(
                  "w-full px-3 py-2 border rounded-lg",
                  !!timestampFrom || !!timestampTo
                    ? "bg-[var(--gray-a3)] border-[var(--gray-a7)] text-[var(--gray-a11)] cursor-not-allowed"
                    : "bg-[var(--gray-a1)] border-[var(--gray-a7)]"
                )}
                type="datetime-local"
                disabled={!!timestampFrom || !!timestampTo}
                min={getBufferedMinDate()?.format("YYYY-MM-DDTHH:mm") || ""}
                max={getBufferedMaxDate()?.format("YYYY-MM-DDTHH:mm") || ""}
              />
            </div>
            <div>
              <input
                value={timestampFrom}
                placeholder="2024-05-17T09:31:04.084Z"
                onChange={(e) => {
                  const value = e.target.value;
                  setTimestampFrom(value);
                  if (value) {
                    setTimezone(utcTimezone);
                    setPreset("");
                  } else if (!value && !timestampTo) {
                    setTimezone(getTimezone());
                  }
                }}
                onKeyDown={handleInputKeyDown}
                className="w-full px-3 py-2 border border-[var(--gray-a7)] bg-[var(--gray-a1)] rounded-lg"
              />
            </div>
          </div>
          <div className="mb-5">
            <label htmlFor="to" className="block text-sm mb-2">
              To
            </label>
            <div className="flex gap-2 mb-2">
              <input
                id="to"
                step="1"
                value={
                  innerValue[1]
                    ? innerValue[1].format("YYYY-MM-DDTHH:mm:ss")
                    : ""
                }
                onChange={(event) => {
                  const newTo = clampDate(dayjs(event.target.value));
                  setInnerValue((currentValue) => [
                    currentValue[0] && currentValue[0] < newTo
                      ? currentValue[0]
                      : clampDate(
                          dayjs(event.target.value).subtract(1, "second")
                        ),
                    newTo,
                  ]);
                }}
                className={twMerge(
                  "w-full px-3 py-2 border rounded-lg",
                  !!timestampFrom || !!timestampTo
                    ? "bg-[var(--gray-a3)] border-[var(--gray-a7)] text-[var(--gray-a11)] cursor-not-allowed"
                    : "bg-[var(--gray-a1)] border-[var(--gray-a7)]"
                )}
                type="datetime-local"
                disabled={!!timestampFrom || !!timestampTo}
                min={getBufferedMinDate()?.format("YYYY-MM-DDTHH:mm") || ""}
                max={getBufferedMaxDate()?.format("YYYY-MM-DDTHH:mm") || ""}
              />
            </div>
            <div>
              <input
                value={timestampTo}
                placeholder="2024-05-17T09:36:10.327Z"
                onChange={(e) => {
                  const value = e.target.value;
                  setTimestampTo(value);
                  if (value) {
                    setTimezone(utcTimezone);
                    setPreset("");
                  } else if (!value && !timestampFrom) {
                    setTimezone(getTimezone());
                  }
                }}
                onKeyDown={handleInputKeyDown}
                className="w-full px-3 py-2 border border-[var(--gray-a7)] bg-[var(--gray-a1)] rounded-lg"
              />
            </div>
          </div>
          {showTimezoneSelector && (
            <div className="mb-2">
              <label htmlFor="timezone" className="block text-sm mb-2">
                Timezone
              </label>
              <div className="flex gap-2">
                <TimezoneSelect
                  id="timezone"
                  className="w-full"
                  classNamePrefix="tezzro"
                  isDisabled={!!timestampFrom || !!timestampTo}
                  value={timezone}
                  onChange={(value) => setTimezone(value)}
                />
              </div>
            </div>
          )}
        </div>

        {isError && (
          <p className="px-4 text-sm mb-4 font-medium text-red-500">
            Start date must be greater than to date
          </p>
        )}

        {showPresetSelector && (
          <>
            <Separator size="4" />
            <div className="p-4">
              <label className="block text-sm mb-2">Preset</label>
              <div className="flex items-center gap-2">
                <Select.Root
                  size="3"
                  value={preset}
                  onValueChange={(value) => {
                    setPreset(value);

                    setTimestampFrom("");
                    setTimestampTo("");
                    const selectedPreset = presets?.find(
                      (preset) => preset.value === value
                    );

                    if (selectedPreset) {
                      const [start, end, timezone] =
                        selectedPreset.value.split("__");
                      setInnerValue([dayjs(start), dayjs(end)]);
                      setTimezone(timezone || getTimezone());
                    }
                  }}
                >
                  <Select.Trigger
                    className="grow max-w-[220px]"
                    placeholder="Select preset"
                  />
                  <Select.Content className="z-20">
                    {presets?.map((preset) => (
                      <Select.Item key={preset.value} value={preset.value}>
                        {preset.label}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
                <Tooltip content="Add new">
                  <IconButton
                    onClick={onAddPreset}
                    size="3"
                    variant="soft"
                    color="gray"
                  >
                    <PlusIcon width={18} height={18} />
                  </IconButton>
                </Tooltip>
                <ImportJsonPresets onSave={setValue} />
              </div>
            </div>
          </>
        )}

        <Separator size="4" />

        <div className="p-3 flex justify-between">
          <RButton
            variant="soft"
            color="gray"
            className="rounded-lg"
            onClick={onClear}
          >
            Clear
          </RButton>
          <RButton
            color="gray"
            disabled={isError}
            highContrast
            className="rounded-lg"
            onClick={() => onApply([innerValue[0], innerValue[1]], timezone)}
          >
            Apply
          </RButton>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}
